'use client'

import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { useRouter } from '@/navigation'
import { resolvePostAuthRedirectPath } from '@/services/auth/clientAuthState'
import { AUTH_REDIRECT_QUERY_PARAM } from '@/services/auth/redirectPath'
import {
  clearPublicAuthLocalState,
  completeAuthNavigation,
} from '@/services/auth/postAuth.client'
import {
  consumeRedirectAttempted,
  markRedirectAttempted,
} from '@/services/auth/publicAuthRedirectMarker.client'

function getSafePublicRedirectPath(): string {
  if (typeof window === 'undefined') return '/'

  const params = new URLSearchParams(window.location.search)
  return resolvePostAuthRedirectPath(params.get(AUTH_REDIRECT_QUERY_PARAM))
}

export default function PublicAuthSessionGate() {
  const queryClient = useQueryClient()
  const router = useRouter()

  useEffect(() => {
    let isMounted = true

    const bootstrap = async () => {
      const redirectPath = getSafePublicRedirectPath()

      if (consumeRedirectAttempted()) {
        await clearPublicAuthLocalState(queryClient)
        return
      }

      if (!isMounted) return

      await completeAuthNavigation({
        router,
        safeRedirectPath: redirectPath,
        queryClient,
        onBeforeRedirect: markRedirectAttempted,
      })
    }

    void bootstrap()

    return () => {
      isMounted = false
    }
  }, [queryClient, router])

  return null
}
